import { apiFetch } from "@pontimed/api-client";
import { createFileRoute, redirect } from "@tanstack/react-router";

import { i18n } from "@/i18n/i18n";
import { readBrowsingCountryFromCookie } from "@/state/country";
import { LEGACY_ACCOUNT_PREFIX } from "@/lib/account-path";

interface ResolveResponse {
  path: string;
}

/**
 * Legacy `/account/notifications` forwarded to the user's preferred workspace
 * notifications page based on `lastWorkspaceType` and kept the FULL query
 * string (legacy src/app/[locale]/[country]/account/notifications/page.tsx):
 *   provider → /account/provider/notifications
 *   facility → /account/facility/{id}/notifications
 *   else     → /account/user/notifications
 *
 * `/api/account/resolve-workspace` re-validates lastWorkspaceType/Id against
 * the DB and returns the workspace ROOT path prefixed with the legacy
 * `/<locale>/<country>` pair (which we strip — apps/app routes are
 * unprefixed), same as the account index route. Any failure (API down,
 * unauthenticated race) falls back to user notifications.
 */
export const Route = createFileRoute("/_authenticated/account/notifications")({
  // Pass-through validator so beforeLoad sees the raw incoming search and can
  // forward it wholesale (legacy parity).
  validateSearch: (search: Record<string, unknown>) => search,
  beforeLoad: async ({ search }) => {
    let root = "/account/user";
    try {
      // locale/country only shaped the `/<locale>/<country>` prefix older API
      // versions emitted; the current one ignores them.
      const locale = i18n.locale || "en";
      const country = readBrowsingCountryFromCookie() ?? "ro";
      const { path } = await apiFetch.get<ResolveResponse>(
        `/api/account/resolve-workspace?locale=${encodeURIComponent(locale)}&country=${encodeURIComponent(country)}`,
      );
      const stripped = path.replace(LEGACY_ACCOUNT_PREFIX, "");
      if (stripped.startsWith("/account/")) root = stripped;
    } catch {
      /* fall back to user notifications */
    }

    // Forward the FULL incoming query string (legacy parity). Target routes
    // re-validate `tab` at runtime, so the loose cast is safe.
    const forwardedSearch = search as never;

    if (root === "/account/provider") {
      throw redirect({ to: "/account/provider/notifications", search: forwardedSearch });
    }
    const facilityId = /^\/account\/facility\/([^/]+)$/.exec(root)?.[1];
    if (facilityId) {
      throw redirect({
        to: "/account/facility/$facilityId/notifications",
        params: { facilityId },
        search: forwardedSearch,
      });
    }
    throw redirect({ to: "/account/user/notifications", search: forwardedSearch });
  },
});
